import { useEffect, useMemo, useState } from 'react';

import { IconX } from './icons';
import { SUB_CATEGORIES_BY_CATEGORY } from './incidentLabels';
import { useAccount } from '../../contexts/AccountContext';
import { useToast } from '../../contexts/ToastContext';
import { useCreateIncident } from '../../services/api/hooks/useCreateIncident';
import { useRides } from '../../services/api/hooks/useRides';
import type { IncidentCategory, IncidentSubCategory } from '../../types/extranet-api.types';
import { useFilterStore } from '../filters/filterStore';
import { getCity, getExternalRef } from '../rides/trip';

interface CreateIncidentModalProps {
  initialRideId?: string;
  onClose: () => void;
  onSuccess?: (incidentId: string) => void;
}

const MIN_REPORT = 10;
const MAX_REPORT = 5000;

export const CreateIncidentModal = ({ initialRideId, onClose, onSuccess }: CreateIncidentModalProps) => {
  const { config } = useAccount();
  const toast = useToast();
  const { entityIds, contractIds, siteIds, dateRange } = useFilterStore();

  const [rideId, setRideId] = useState(initialRideId ?? '');
  const [search, setSearch] = useState('');
  const [clientReport, setClientReport] = useState('');
  const [category, setCategory] = useState<IncidentCategory | ''>('');
  const [subCategory, setSubCategory] = useState<IncidentSubCategory | ''>('');
  const [touched, setTouched] = useState(false);

  const mutation = useCreateIncident();

  // Recherche de courses si pas de rideId
  const ridesQuery = useRides({
    entityIds,
    contractIds,
    siteIds,
    dateRange,
    page: 1,
    limit: 6,
    search: search || undefined,
    sortBy: 'pickupAt',
    sortDir: 'desc',
  });
  const rides = ridesQuery.data?.items ?? [];

  const subCategoryOptions = useMemo(() => (category ? SUB_CATEGORIES_BY_CATEGORY[category] : []), [category]);

  useEffect(() => {
    // si la category change, reset la sous-cat
    if (category && subCategory && !SUB_CATEGORIES_BY_CATEGORY[category].includes(subCategory)) {
      setSubCategory('');
    }
  }, [category, subCategory]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  const reportError =
    clientReport.length > 0 && (clientReport.length < MIN_REPORT || clientReport.length > MAX_REPORT)
      ? `Remontée client : ${MIN_REPORT} à ${MAX_REPORT} caractères (actuel : ${clientReport.length})`
      : null;

  const canSubmit =
    Boolean(rideId) &&
    Boolean(category) &&
    Boolean(subCategory) &&
    clientReport.length >= MIN_REPORT &&
    clientReport.length <= MAX_REPORT &&
    !mutation.isPending;

  const isSecurity = category === 'security';

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (!canSubmit) return;
    mutation.mutate(
      {
        rideId,
        category: category as IncidentCategory,
        subCategory: subCategory as IncidentSubCategory,
        clientReport: clientReport.trim(),
      },
      {
        onSuccess: detail => {
          toast.success('Réclamation enregistrée', `Ticket ${detail.id} créé. Email d'accusé de réception envoyé.`);
          onSuccess?.(detail.id);
          onClose();
        },
        onError: err => {
          toast.error('Échec de la création', (err as Error).message || 'Réessayez ou contactez votre administrateur.');
        },
      },
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-[10vh] px-4">
      <div
        className="absolute inset-0 bg-black/30 drawer-backdrop-in"
        style={{ backdropFilter: 'blur(2px)' }}
        onClick={onClose}
        aria-hidden
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="create-incident-title"
        className="relative w-full max-w-[520px] bg-white border border-zinc-200 rounded-md shadow-md drawer-panel-in flex flex-col max-h-[80vh]"
      >
        <header className="sticky top-0 bg-white px-6 py-4 border-b border-zinc-100 flex items-center justify-between">
          <h2 id="create-incident-title" className="text-base font-semibold text-zinc-900">
            Nouvelle réclamation
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fermer"
            className="h-8 w-8 inline-flex items-center justify-center rounded-md text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100 transition-colors"
          >
            <IconX width={16} height={16} />
          </button>
        </header>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-5 flex flex-col gap-4">
          {/* Course */}
          <div className="flex flex-col gap-1.5">
            <label htmlFor="incident-ride" className="text-xs font-medium text-zinc-700">
              Course
            </label>
            {rideId && initialRideId && rideId === initialRideId ? (
              <div className="h-10 px-3 flex items-center bg-zinc-50 border border-zinc-200 rounded-md text-sm font-mono tabular-nums text-zinc-900">
                {rideId}
                <button
                  type="button"
                  onClick={() => setRideId('')}
                  className="ml-auto text-xs text-zinc-500 hover:text-zinc-900 underline"
                >
                  Changer
                </button>
              </div>
            ) : (
              <>
                <input
                  id="incident-ride"
                  type="text"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Rechercher par N° course ou GALAC'SI"
                  className="h-10 w-full px-3 text-sm bg-white border border-zinc-300 rounded-md text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:border-zinc-400 focus:ring-2 focus:ring-zinc-900/10 transition-colors"
                />
                {rides.length > 0 ? (
                  <ul className="border border-zinc-200 rounded-md max-h-44 overflow-y-auto bg-white">
                    {rides.map(trip => (
                      <li key={trip.id}>
                        <button
                          type="button"
                          onClick={() => {
                            setRideId(trip.id);
                            setSearch('');
                          }}
                          className={[
                            'w-full px-3 py-2 flex items-center gap-3 text-left text-sm transition-colors',
                            rideId === trip.id ? 'bg-zinc-50 text-zinc-900' : 'text-zinc-700 hover:bg-zinc-50',
                          ].join(' ')}
                        >
                          <span className="font-mono tabular-nums text-xs text-zinc-900">{trip.id.slice(-12)}</span>
                          <span className="text-zinc-300">·</span>
                          <span className="font-mono tabular-nums text-xs text-zinc-500">{getExternalRef(trip)}</span>
                          <span className="ml-auto text-xs text-zinc-500 truncate max-w-[180px]">{getCity(trip)}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : null}
                {rideId && <p className="text-xs text-zinc-500 font-mono tabular-nums">Sélectionnée : {rideId}</p>}
                {touched && !rideId ? <p className="text-xs text-red-600">Une course doit être sélectionnée.</p> : null}
              </>
            )}
          </div>

          {/* Remontée client */}
          <div className="flex flex-col gap-1.5">
            <div className="flex items-baseline justify-between">
              <label htmlFor="incident-report" className="text-xs font-medium text-zinc-700">
                Remontée client
              </label>
              <span
                className={['text-xs font-mono tabular-nums', reportError ? 'text-red-600' : 'text-zinc-500'].join(' ')}
              >
                {clientReport.length} / {MAX_REPORT}
              </span>
            </div>
            <textarea
              id="incident-report"
              value={clientReport}
              onChange={e => setClientReport(e.target.value)}
              rows={4}
              placeholder="Verbatim ou résumé de la réclamation transmise par le client (10-5000 caractères)."
              className="w-full px-3 py-2 text-sm bg-white border border-zinc-300 rounded-md text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:border-zinc-400 focus:ring-2 focus:ring-zinc-900/10 transition-colors resize-y"
            />
            {reportError ? <p className="text-xs text-red-600">{reportError}</p> : null}
          </div>

          {/* Catégorie + sous-catégorie */}
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1.5">
              <label htmlFor="incident-category" className="text-xs font-medium text-zinc-700">
                Catégorie
              </label>
              <select
                id="incident-category"
                value={category}
                onChange={e => setCategory(e.target.value as IncidentCategory | '')}
                className="h-10 px-2 text-sm bg-white border border-zinc-300 rounded-md text-zinc-900 focus:outline-none focus:border-zinc-400 focus:ring-2 focus:ring-zinc-900/10 transition-colors"
              >
                <option value="">— Choisir —</option>
                {config.incidentCategories.map(c => (
                  <option key={c.category} value={c.category}>
                    {c.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="incident-subcategory" className="text-xs font-medium text-zinc-700">
                Sous-catégorie
              </label>
              <select
                id="incident-subcategory"
                value={subCategory}
                onChange={e => setSubCategory(e.target.value as IncidentSubCategory | '')}
                disabled={!category}
                className="h-10 px-2 text-sm bg-white border border-zinc-300 rounded-md text-zinc-900 focus:outline-none focus:border-zinc-400 focus:ring-2 focus:ring-zinc-900/10 transition-colors disabled:bg-zinc-50 disabled:text-zinc-400 disabled:cursor-not-allowed"
              >
                <option value="">— Choisir —</option>
                {subCategoryOptions.map(sub => {
                  const label =
                    config.incidentCategories
                      .find(c => c.category === category)
                      ?.subCategories.find(s => s.subCategory === sub)?.label ?? sub;
                  return (
                    <option key={sub} value={sub}>
                      {label}
                    </option>
                  );
                })}
              </select>
            </div>
          </div>

          {isSecurity ? (
            <p className="text-xs px-3 py-2 bg-red-50 border border-red-100 text-red-700 rounded-md flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full bg-red-500" aria-hidden />
              Incident sécurité → priorité haute, dérivée automatiquement.
            </p>
          ) : null}

          {mutation.error ? (
            <p className="text-xs px-3 py-2 bg-red-50 border border-red-100 text-red-700 rounded-md">
              {(mutation.error as Error).message || 'Échec de la création. Réessayer.'}
            </p>
          ) : null}
        </form>

        <footer className="px-6 py-3 border-t border-zinc-100 flex items-center justify-end gap-2 bg-white">
          <button
            type="button"
            onClick={onClose}
            className="h-9 px-3 text-sm text-zinc-700 bg-white border border-zinc-200 rounded-md hover:bg-zinc-50 transition-colors"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={!canSubmit}
            className="h-9 px-4 text-sm font-medium bg-zinc-900 text-white rounded-md hover:bg-zinc-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {mutation.isPending ? 'Création…' : 'Confirmer'}
          </button>
        </footer>
      </div>
    </div>
  );
};
